"use client";

import { useEffect, useState } from "react";

// Self-contained password gate for embed contexts. Mirrors the visual identity
// of ResumePlayOverlay: position:absolute (constrained to its parent player
// rect), border-radius:inherit, fully opaque dark backdrop, centered card, no
// external UI library imports so the embed bundle stays lean.
//
// - Real mode (default): submits to /api/video/<id>/verify-access, on success
//   writes the same `video-access-<videoId>` sessionStorage entry that
//   VideoPageClient.js / EmbedClient.jsx use, then calls onAccessGranted with
//   { accessToken, mediaToken, method }.
// - previewMode: skips the API call + sessionStorage entirely; submitting just
//   calls onAccessGranted({ method: "preview" }) so the panel preview can
//   demonstrate the visual without affecting real protection.

// WCAG-style relative luminance — picks readable text on a tinted button.
function relativeLuminance(hex) {
  if (typeof hex !== "string") return 0;
  const cleaned = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return 0;
  const channel = (eight) => {
    const v = eight / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const r = channel(parseInt(cleaned.slice(0, 2), 16));
  const g = channel(parseInt(cleaned.slice(2, 4), 16));
  const b = channel(parseInt(cleaned.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const STYLE_BLOCK = `
.flare-vag-root {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--flare-vag-bg, #0a0a0a);
  border-radius: inherit;
  z-index: 50;
  padding: 24px;
  font-family: inherit;
  -webkit-font-smoothing: antialiased;
  color: var(--flare-vag-fg, #ffffff);
}
.flare-vag-card {
  width: 100%;
  max-width: 380px;
  text-align: center;
}
/* When the customer enables "Show video title", the title takes over the
   heading slot — the lock icon already conveys "this is protected", so
   we drop the generic "This video is protected" line and let the title
   itself be the focal point. Wraps to 2 lines if needed instead of
   eliding so longer filenames stay readable. */
.flare-vag-video-title-heading {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1.2;
  color: var(--flare-vag-fg, #ffffff);
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}
.flare-vag-lock {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
  color: var(--flare-vag-fg-muted, rgba(255, 255, 255, 0.55));
}
.flare-vag-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1.2;
  color: var(--flare-vag-fg, #ffffff);
  margin: 0 0 8px 0;
}
.flare-vag-subtitle {
  font-size: 13px;
  line-height: 1.4;
  color: var(--flare-vag-fg-muted, rgba(255, 255, 255, 0.55));
  margin: 0 0 22px 0;
}
.flare-vag-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}
.flare-vag-input {
  width: 100%;
  height: 2.5rem;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid var(--flare-vag-input-border, rgba(255, 255, 255, 0.12));
  background: var(--flare-vag-input-bg, rgba(255, 255, 255, 0.04));
  color: var(--flare-vag-fg, #ffffff);
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 200ms ease, background-color 200ms ease;
}
.flare-vag-input::placeholder {
  color: var(--flare-vag-fg-muted, rgba(255, 255, 255, 0.35));
}
.flare-vag-input:focus {
  border-color: var(--flare-vag-button, rgba(255, 103, 2, 0.6));
  background: var(--flare-vag-input-bg-focus, rgba(255, 255, 255, 0.06));
}
.flare-vag-input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.flare-vag-error {
  font-size: 12px;
  color: #f87171;
  margin: -2px 0 0 0;
  text-align: left;
}
.flare-vag-btn {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.5rem;
  padding: 0 1rem;
  margin-top: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.5rem;
  border-radius: 13px;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  font-family: inherit;
  color: var(--flare-vag-button-fg, #ffffff);
  background-color: var(--flare-vag-button, #FF6702);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.15),
    0 4px 14px rgba(0, 0, 0, 0.1);
  transition: transform 200ms ease;
}
.flare-vag-btn::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;
  background-color: rgba(255, 255, 255, 0.08);
  opacity: 0;
  transition: opacity 300ms cubic-bezier(0.4, 0.36, 0, 1);
}
.flare-vag-btn:hover::after {
  opacity: 1;
}
.flare-vag-btn:active {
  transform: translateY(0.5px);
}
.flare-vag-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
`;

export default function VideoAccessGate({
  videoId,
  onAccessGranted,
  inviteToken,
  previewMode = false,
  backgroundColor = "#0a0a0a",
  buttonColor = "#FF6702",
  showVideoTitle = false,
  videoTitle = "",
  // Absolute origin for the verify-access API. Defaults to "" (same-origin
  // relative request) for in-app / Next.js /embed renders. The flarefasthost.com
  // iframe bundle passes "https://flare.link" so the POST reaches the app API
  // instead of the edge worker, which has no /api/video/... route.
  apiBaseUrl = "",
}) {
  const [password, setPassword] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState(null);

  const verifyAccess = async (credentials) => {
    if (previewMode) {
      onAccessGranted?.({ method: "preview" });
      return;
    }

    setIsVerifying(true);
    setError("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/video/${videoId}/verify-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.ok && data.accessGranted) {
        const accessData = {
          token: data.accessToken,
          mediaToken: data.mediaToken || null,
          method: data.method,
          grantedAt: Date.now(),
        };
        try {
          sessionStorage.setItem(
            `video-access-${videoId}`,
            JSON.stringify(accessData)
          );
        } catch (_) {}

        onAccessGranted?.(data);
        return;
      }

      if (response.status === 429) {
        setError("Too many attempts. Please try again later.");
        setRemainingAttempts(0);
      } else if (response.status === 401) {
        setError(data.error || "Incorrect password");
        if (typeof data.remainingAttempts === "number") {
          setRemainingAttempts(data.remainingAttempts);
        }
      } else {
        setError(data.error || "Failed to verify access");
      }
    } catch (_) {
      setError("Connection error. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  // Auto-verify if an invite token is present (matches VideoPageClient.js / EmbedClient.jsx).
  useEffect(() => {
    if (previewMode) return;
    if (inviteToken) {
      verifyAccess({ inviteToken });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken, previewMode]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!password.trim()) {
      setError("Please enter a password");
      return;
    }
    verifyAccess({ password: password.trim() });
  };

  const errorMessage = error
    ? remainingAttempts !== null && remainingAttempts > 0
      ? `${error} (${remainingAttempts} attempt${remainingAttempts !== 1 ? "s" : ""} remaining)`
      : error
    : null;

  // Pick a readable foreground for the backdrop and the button based on
  // their luminance. Lets the customer choose any color and still get
  // legible text without needing a separate "text color" picker.
  const bgIsLight = relativeLuminance(backgroundColor) > 0.55;
  const buttonIsLight = relativeLuminance(buttonColor) > 0.55;
  const fg = bgIsLight ? "#0f0f0f" : "#ffffff";
  const fgMuted = bgIsLight ? "rgba(15, 15, 15, 0.55)" : "rgba(255, 255, 255, 0.55)";
  const inputBorder = bgIsLight ? "rgba(0, 0, 0, 0.18)" : "rgba(255, 255, 255, 0.12)";
  const inputBg = bgIsLight ? "rgba(0, 0, 0, 0.04)" : "rgba(255, 255, 255, 0.04)";
  const inputBgFocus = bgIsLight ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.06)";
  const buttonFg = buttonIsLight ? "#0f0f0f" : "#ffffff";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Password protected video"
      className="flare-vag-root"
      style={{
        "--flare-vag-bg": backgroundColor,
        "--flare-vag-fg": fg,
        "--flare-vag-fg-muted": fgMuted,
        "--flare-vag-button": buttonColor,
        "--flare-vag-button-fg": buttonFg,
        "--flare-vag-input-border": inputBorder,
        "--flare-vag-input-bg": inputBg,
        "--flare-vag-input-bg-focus": inputBgFocus,
      }}
    >
      <style>{STYLE_BLOCK}</style>
      <div className="flare-vag-card">
        <div className="flare-vag-lock">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        {showVideoTitle && videoTitle ? (
          <h2 className="flare-vag-video-title-heading">{videoTitle}</h2>
        ) : (
          <h2 className="flare-vag-title">This video is protected</h2>
        )}
        <p className="flare-vag-subtitle">
          This video is password protected. Enter password to watch.
        </p>
        <form className="flare-vag-form" onSubmit={handleSubmit}>
          <input
            type="password"
            className="flare-vag-input"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setError("");
            }}
            placeholder="Password"
            autoComplete="off"
            disabled={isVerifying || remainingAttempts === 0}
          />
          {errorMessage ? (
            <p className="flare-vag-error">{errorMessage}</p>
          ) : null}
          <button
            type="submit"
            className="flare-vag-btn"
            disabled={isVerifying || remainingAttempts === 0}
          >
            {isVerifying ? "Verifying…" : "Unlock video"}
          </button>
        </form>
      </div>
    </div>
  );
}
